import { FunctionComponent, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { formatDistanceToNow } from "date-fns";

import { classNames } from "@/utils/classNames";

import { useMarkAsReadMutation } from "@/pages/Private/redux/notification/notification.api";

import { formatNotificationMessage } from "@/utils/formatNotifs";

import { Notification } from "../schema/notification";

interface NotificationItemProps {
	notification: Notification;
	onClose?: () => void;
}

export const NotificationItem: FunctionComponent<NotificationItemProps> = ({
	notification,
	onClose,
}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [markAsRead] = useMarkAsReadMutation();

	const handleClick = useCallback(async () => {
		if (!notification.isRead) {
			try {
				await markAsRead(notification.id);
			} catch (e) {
				console.error("Failed to mark notification as read:", e);
			}
		}

		if (notification.link) {
			onClose?.();

			// The backend may return links like "app/lists/detail/42" or
			// "/app/lists/detail/42". Normalise to an absolute path so
			// React Router doesn't resolve it relative to the current route.
			let link = notification.link;

			if (!link.startsWith("/")) {
				link = `/${link}`;
			}

			navigate(link);
		}
	}, [notification, markAsRead, navigate, onClose]);

	const relativeTime = formatDistanceToNow(new Date(notification.createdAt), {
		addSuffix: true,
	});

	const aggregatedCount = notification.metadata?.aggregatedCount as number | undefined;

	const displayMessage = formatNotificationMessage(notification.message, notification.metadata, t);

	return (
		<div
			className={classNames(
				"flex flex-row items-start gap-3 px-4 py-3 border-b border-[#EAECF0] cursor-pointer transition-colors duration-150",
				notification.isRead ? "bg-white hover:bg-gray-50" : "bg-blue-50 hover:bg-blue-100"
			)}
			onClick={handleClick}
		>
			{/* Priority dot */}
			<div className="flex-shrink-0 pt-1.5">
				<div className={classNames("w-2.5 h-2.5 rounded-full")} />
			</div>

			{/* Content */}
			<div className="flex-1 min-w-0">
				<div className="flex flex-row items-start justify-between gap-2">
					<p
						className={classNames(
							"text-sm leading-tight truncate",
							notification.isRead ? "font-normal text-gray-700" : "font-semibold text-gray-900"
						)}
					>
						{notification.title || t("notifications.item.fallbackTitle")}
					</p>
					<span className="text-xs text-text-meta whitespace-nowrap flex-shrink-0">
						{relativeTime}
					</span>
				</div>

				{displayMessage && (
					<p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{displayMessage}</p>
				)}

				{aggregatedCount && aggregatedCount > 1 && (
					<span className="inline-block mt-1 text-xs text-blue-600 bg-blue-100 rounded px-1.5 py-0.5">
						{t("notifications.item.aggregatedItems", { count: aggregatedCount })}
					</span>
				)}
			</div>

			{/* Unread indicator */}
			{!notification.isRead && (
				<div className="flex-shrink-0 pt-1.5">
					<div className="w-2 h-2 rounded-full bg-blue-action" />
				</div>
			)}
		</div>
	);
};
